export default function ApacheJunctionTravelTimingSection() {
  return (
    <section className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Regional Travel Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Travel &amp; Passport Timing for Apache Junction Residents
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Understanding how Apache Junction travelers actually move helps us anticipate the passport urgencies that arise here. From snowbird migration timelines to Baja California road trips, Pinal County travel follows patterns that are unlike most Arizona markets.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          {/* PHX & Mesa Gateway */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">PHX &amp; Mesa Gateway Airport</h3>
                <p className="text-gray-400 text-xs">Phoenix Sky Harbor 35 miles west · Mesa Gateway (AZA) 15 miles west</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Apache Junction residents use both Phoenix Sky Harbor (PHX) and Mesa Gateway Airport (AZA) depending on their airline and destination. PHX handles the majority of international flights including direct routes to Canada, Mexico, and Europe. Mesa Gateway serves budget carriers with routes to U.S. cities. For snowbirds flying home to Calgary or Toronto, PHX is the primary gateway — and a last-minute passport discovery at the departure gate is more common than travelers expect.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Canada and Mexico both require a valid U.S. passport. Check expiration dates before booking — many countries also require six months of remaining validity beyond your return date.
              </p>
            </div>
          </div>

          {/* Snowbird Season */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-sun-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Snowbird Migration Season (Oct–Apr)</h3>
                <p className="text-gray-400 text-xs">Canadian and northern U.S. residents wintering in Apache Junction</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Apache Junction has one of the highest concentrations of Canadian snowbirds in Arizona. Many spend November through April in Gold Canyon, Queen Valley, and the Superstition Mountain resort communities before returning north. The passport urgency window opens every September and October, when snowbirds realize their Canadian passport (or their U.S. passport, for those with dual citizenship) is expired or expiring mid-winter trip. We process numerous expedited renewals for this community every fall.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> If you are a U.S. citizen who winters in Arizona and summers in Canada, make sure your U.S. passport is valid for the full duration of your stay in either country — and does not expire mid-trip.
              </p>
            </div>
          </div>

          {/* Baja & Rocky Point RV Travel */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-car-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Baja California &amp; Rocky Point RV Travel</h3>
                <p className="text-gray-400 text-xs">Puerto Peñasco ~180 miles · Baja coastal route via Nogales ~200+ miles</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Apache Junction has a strong RV culture, with a significant share of residents making winter road trips to Puerto Peñasco (Rocky Point) and Baja California. While the U.S.-Mexico land border can technically be crossed with a Passport Card or Enhanced Driver's License, many Apache Junction RV travelers prefer a full passport book for flexibility — especially if they plan to fly home from a remote Mexican coastal area or need to access U.S. consular services during an extended stay.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> A Passport Card works at land and sea borders but is NOT accepted for international air travel. If your Baja trip includes any possibility of flying home, a full passport book is essential.
              </p>
            </div>
          </div>

          {/* East Valley Connection */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-building-2-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">East Valley Business &amp; ASU Community</h3>
                <p className="text-gray-400 text-xs">Mesa tech corridor, ASU Polytechnic, East Valley workers</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              The eastern edge of the Phoenix metro, including workers who commute to Chandler, Gilbert, and Mesa, creates a business travel demand in Apache Junction that is often overlooked. Intel, Boeing, and other East Valley manufacturers and tech firms send employees to international facilities in Mexico, Ireland, and Taiwan. ASU Polytechnic campus workers and students also travel for research and study abroad. These travelers need expedited passports on corporate timelines — not six-week government queues.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Corporate travel to Mexico City or Dublin frequently has 7–14 day lead times. Start the expedited process the moment your travel is confirmed — do not wait for flights to be booked.
              </p>
            </div>
          </div>
        </div>

        {/* Superstition Mountains / Outdoor Adventure callout */}
        <div className="bg-[#1A365D] rounded-lg p-7 flex flex-col md:flex-row items-center gap-6">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
            <i className="ri-landscape-line text-[#F6C344] text-2xl"></i>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-bold text-white text-base mb-2">Outdoor Adventure &amp; International Expeditions</h3>
            <p className="text-white/60 text-sm leading-relaxed">
              Apache Junction is a basecamp for outdoor enthusiasts who don&apos;t stay local. International hiking and adventure travel to Patagonia, the Canadian Rockies, Costa Rica, and New Zealand draws heavily from the Superstition Mountain outdoor community. Multi-country expeditions require valid passports with significant remaining validity — often six months or more per destination. We handle expedited renewals and new applications for adventure travelers whose timelines are set by seasonal conditions, not government queues.
            </p>
          </div>
          <a
            href="https://fastpassportcenter.com/checkout/"
            className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap flex-shrink-0"
          >
            Get Started <i className="ri-arrow-right-line ml-1"></i>
          </a>
        </div>
      </div>
    </section>
  );
}